/**
 * Certificate Add-on Feature
 * Combined logic for Configuration, PDP Add-to-Cart, and Cart Synchronization.
 */

// 1. Configuration
const certAddonConfig = window.certAddonConfig || {
  enabled: true,

  // REQUIRED: Replace with the actual Variant ID of the certificate product
  certificateVariantId: 57269496414553,

  // PDP checkbox detection
  checkboxBlockId: "line_item_property_TzhBxL",
  checkboxInputSelector: 'input[type="checkbox"]',

  // Link properties (internal)
  linkIdProp: "_cert_link_id",
  parentMarkerProp: "_has_cert",
  childMarkerProp: "_is_cert",
  childForVariantProp: "_cert_for_variant",

  // Logic settings
  syncQuantities: true,
  cleanupOrphans: true,
  debug: false,
};
window.certAddonConfig = certAddonConfig;

const getUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getBundledSections = () => {
  let sectionsToBundle = new Set();
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
    bubbles: true,
    detail: { sections: sectionsToBundle }
  }));
  if (sectionsToBundle.size === 0) {
    const commonSections = ["cart-drawer", "cart-count", "header"];
    commonSections.forEach((selector) => {
      const element = document.querySelector(selector);
      if (!element) return;
      const section = element.closest(".shopify-section");
      if (section?.id) sectionsToBundle.add(section.id.replace("shopify-section-", ""));
    });
  }
  return [...sectionsToBundle];
};

const showError = (form, message) => {
  form.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { error: message } }));
  document.dispatchEvent(new CustomEvent("cart:error", { detail: { error: message } }));
  document.dispatchEvent(new CustomEvent("toast:show", { detail: { message, tone: "error" } }));
  document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
};

const ensureSections = (cart, sectionsHtml) => {
  if (!cart.sections) cart.sections = {};
  cart.sections = sectionsHtml;
  return cart;
};

const patchRecommendationsFetch = () => {
  if (window.__certRecommendationsFetchPatched) return;
  window.__certRecommendationsFetchPatched = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
      if (url && url.includes("/recommendations/products")) {
        const text = await response.clone().text();
        if (!text.includes("product-recommendations")) {
          return new Response("<product-recommendations></product-recommendations>", {
            status: response.status,
            statusText: response.statusText,
            headers: new Headers(response.headers)
          });
        }
      }
    } catch (e) {
      if (certAddonConfig.debug) console.warn("[CertAddon] recommendations fetch patch error", e);
    }
    return response;
  };
};

// 2. PDP Add-to-Cart Logic
(() => {
  if (!certAddonConfig.enabled) return;
  patchRecommendationsFetch();

  const handleAddToCart = async (event) => {
    const form = event.target;
    
    // Only intercept forms posting to cart/add
    if (!form.action || !form.action.includes("/cart/add")) return;

    // Check for the certificate checkbox
    const certBlock = document.querySelector(`[data-block-id="${certAddonConfig.checkboxBlockId}"]`);
    const checkbox = certBlock?.querySelector(certAddonConfig.checkboxInputSelector);

    // If checkbox doesn't exist or isn't checked, let the theme handle it normally
    if (!checkbox || !checkbox.checked) return;

    // Stop the theme's default handler
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.submitter?.getAttribute("aria-busy") === "true") return;
    if (!form.checkValidity()) return form.reportValidity();

    const showLoadingBar = event.submitter?.querySelector("button-content") === null;
    let loadingStarted = false;
    const submitButtons = Array.from(form.elements).filter((button) => button.type === "submit");
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("aria-busy", "true");
    });

    try {
      if (certAddonConfig.debug) console.log("[CertAddon] Intercepted Add to Cart");

      const formData = new FormData(form);
      const parentId = formData.get("id");
      const quantity = parseInt(formData.get("quantity") || 1, 10);
      const linkId = getUUID();

      if (!parentId) {
        throw new Error("Variant ID is missing");
      }

      // 1) Prepare sections
      const sections = getBundledSections();
      formData.set("sections", sections.join(","));

      // 2) Add parent
      formData.set(`properties[${certAddonConfig.parentMarkerProp}]`, "1");
      formData.set(`properties[${certAddonConfig.linkIdProp}]`, linkId);

      if (certAddonConfig.debug) console.log("[CertAddon] Adding parent");

      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
        loadingStarted = true;
      }

      const parentResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!parentResponse.ok) {
        const errorData = await parentResponse.json();
        const errorMessage = errorData["message"] || errorData["description"] || "Failed to add parent product";
        showError(form, errorMessage);
        return;
      }
      const parentResponseJson = await parentResponse.json();

      // 3) Add certificate (can fail without removing parent)
      const certPayload = {
        id: certAddonConfig.certificateVariantId,
        quantity: quantity,
        properties: {
          [certAddonConfig.childMarkerProp]: "1",
          [certAddonConfig.linkIdProp]: linkId,
          [certAddonConfig.childForVariantProp]: parentId
        },
        sections: sections.join(",")
      };

      if (certAddonConfig.debug) console.log("[CertAddon] Adding certificate", certPayload);

      const certResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(certPayload)
      });

      let certResponseJson = null;
      if (!certResponse.ok) {
        console.warn("[CertAddon] Failed to add certificate. Check Variant ID and Stock.", await certResponse.text());
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: { message: "Le certificat n’a pas pu être ajouté. Veuillez réessayer.", tone: "error" }
        }));
      } else {
        certResponseJson = await certResponse.json();
      }

      // 4) Refresh cart UI with sections
      if (showLoadingBar && loadingStarted) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
        loadingStarted = false;
      }

      if (window.themeVariables?.settings?.cartType === "page" || window.themeVariables?.settings?.pageType === "cart") {
        return window.location.href = `${Shopify.routes.root}cart`;
      }

      const sectionsHtml = certResponseJson?.sections || parentResponseJson?.sections;
      if (!sectionsHtml) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        return;
      }
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        return;
      }
      const cart = ensureSections(await cartResponse.json(), sectionsHtml);

      const items = parentResponseJson.hasOwnProperty("items") ? parentResponseJson["items"] : [parentResponseJson];
      form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items,
          cart: cart
        }
      }));

      // Dispatch event compatible with Stretch theme
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cart
        }
      }));

      // Also trigger toast for success (parent added)
      if (window.themeVariables?.settings?.cartType === "message") {
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: {
            message: window.themeVariables?.strings?.addedToCart?.replace("{{product_title}}", items?.[0]?.title) || "Added to cart"
          }
        }));
      } else if (window.themeVariables?.settings?.cartType === "drawer" && typeof announceStatus === "function") {
        let content = [];
        items.forEach((item) => {
          content.push(window.themeVariables?.strings?.addedToCart?.replace("{{product_title}}", item?.title || "") || "");
        });
        announceStatus(content.join(", "));
      }

    } catch (error) {
      console.error("[CertAddon] Error:", error);
      document.dispatchEvent(new CustomEvent("toast:show", {
        detail: { message: error.message || "Error adding to cart", tone: "error" }
      }));
    } finally {
      submitButtons?.forEach((submitButton) => submitButton.removeAttribute("aria-busy"));
      if (showLoadingBar && loadingStarted) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }
  };

  // Use capture phase to intercept before theme's ProductForm
  document.addEventListener("submit", handleAddToCart, true);
})();

// 3. Cart Synchronization Logic
(() => {
  if (!certAddonConfig.enabled) return;

  window.__certSyncLock = false;
  let cartSyncTimer = null;
  let cartSyncInFlight = false;

  const syncCertificateLinks = async (cart) => {
    if (window.__certSyncLock) return;
    window.__certSyncLock = true;

    try {
      if (certAddonConfig.debug) console.log("[CertAddon] Syncing cart...", cart);

      const parentsByLink = new Map();
      const certsByLink = new Map();
      const updates = {};
      let hasChanges = false;

      // Step 1: Map items
      cart.items.forEach((item) => {
        const props = item.properties || {};
        const linkId = props[certAddonConfig.linkIdProp];

        if (linkId) {
          if (props[certAddonConfig.parentMarkerProp] === "1") {
            parentsByLink.set(linkId, item);
          } else if (props[certAddonConfig.childMarkerProp] === "1" && String(item.variant_id) === String(certAddonConfig.certificateVariantId)) {
            if (!certsByLink.has(linkId)) {
              certsByLink.set(linkId, []);
            }
            certsByLink.get(linkId).push(item);
          }
        }
      });

      // Step 2: Sync Quantities (Parent -> Child)
      if (certAddonConfig.syncQuantities) {
        parentsByLink.forEach((parent, linkId) => {
          const certs = certsByLink.get(linkId);
          if (certs && certs.length > 0) {
            // Ensure 1:1 match. If multiple certs for same link (shouldn't happen), keep first, kill others.
            const primaryCert = certs[0];
            
            if (primaryCert.quantity !== parent.quantity) {
              updates[primaryCert.key] = parent.quantity;
              hasChanges = true;
            }

            // Remove duplicates
            for (let i = 1; i < certs.length; i++) {
              updates[certs[i].key] = 0;
              hasChanges = true;
            }
          }
        });
      }

      // Step 3: Cleanup Orphans (Child with no Parent)
      if (certAddonConfig.cleanupOrphans) {
        certsByLink.forEach((certs, linkId) => {
          if (!parentsByLink.has(linkId)) {
            certs.forEach(cert => {
              updates[cert.key] = 0;
              hasChanges = true;
            });
          }
        });
      }

      // Step 4: Apply Updates
      if (hasChanges) {
        if (certAddonConfig.debug) console.log("[CertAddon] Applying updates:", updates);

        const sections = getBundledSections();
        let newCart = cart;

        for (const [lineKey, quantity] of Object.entries(updates)) {
          const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: lineKey,
              quantity: quantity,
              sections: sections.join(",")
            })
          });
          newCart = await response.json();
        }

        if (window.themeVariables?.settings?.pageType === "cart") {
          window.location.reload();
          return;
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "cert-addon:sync",
            cart: newCart
          }
        }));
      }

    } catch (e) {
      console.error("[CertAddon] Sync error:", e);
    } finally {
      window.__certSyncLock = false;
    }
  };

  const runCartSync = async () => {
    if (window.__certSyncLock || cartSyncInFlight) return;
    cartSyncInFlight = true;
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (!response.ok) {
        if (certAddonConfig.debug) console.warn("[CertAddon] cart.js fetch failed", response.status);
        return;
      }
      const cart = await response.json();
      syncCertificateLinks(cart);
    } catch (e) {
      if (certAddonConfig.debug) console.warn("[CertAddon] cart.js fetch error", e);
    } finally {
      cartSyncInFlight = false;
    }
  };

  // Listen for cart changes from the theme
  document.documentElement.addEventListener("cart:change", (event) => {
    if (event.detail?.baseEvent === "cert-addon:sync") return;
    if (event.detail?.cart) {
      syncCertificateLinks(event.detail.cart);
      return;
    }
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(runCartSync, 400);
  });

  const scheduleCartSync = () => {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(runCartSync, 200);
  };

  if (window.themeVariables?.settings?.pageType === "cart") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", scheduleCartSync, { once: true });
    } else {
      scheduleCartSync();
    }
    window.addEventListener("pageshow", scheduleCartSync);
  }

})();
